import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Stack, Divider, IconButton, Tooltip, Chip,
} from '@mui/material';
import { keyframes } from '@mui/system';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

export const SIDEBAR_W     = 240;
export const SIDEBAR_W_MIN = 72;

const ALL_NAV = [
  { label: 'Tableau de bord', icon: <DashboardOutlinedIcon />, key: 'dashboard' },
  { label: 'Gérer Entité',    icon: <BusinessOutlinedIcon />,  key: 'manage', adminOnly: true },
  { label: 'Evenement',       icon: <EmojiEventsOutlinedIcon />, key: 'event' },
  { label: 'Paramètres',      icon: <SettingsOutlinedIcon />,  key: 'settings' },
];

const ROLE_LABEL = { admin: 'Administrateur', player: 'Joueur', coach: 'Entraîneur' };
const ROLE_COLOR = { admin: '#16a34a', player: '#22c55e', coach: '#4ade80' };

const Sidebar = ({ activeSection, onNavigate, mobileOpen, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  const user = (() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) return JSON.parse(stored);
    } catch {}
    return {};
  })();

  const role = user.role || 'player';
  const canSeeEntityManagement = role === 'entityPlatformAdmin' || role === 'admin';
  const NAV = ALL_NAV.filter(item => !item.adminOnly || canSeeEntityManagement);
  const width = collapsed ? SIDEBAR_W_MIN : SIDEBAR_W;

  const handleLogout = () => {
    localStorage.clear();
    navigate('/auth');
  };

  const content = (
    <Box sx={{
      width, height: '100%', display: 'flex', flexDirection: 'column',
      bgcolor: '#ffffff',
      borderRight: '1px solid rgba(34,197,94,.12)',
      boxShadow: '2px 0 16px rgba(34,197,94,.06)',
      transition: 'width .3s cubic-bezier(.4,0,.2,1)', overflow: 'hidden',
    }}>
      {/* Top shimmer line */}
      <Box sx={{
        height: 3, flexShrink: 0,
        background: 'linear-gradient(90deg, transparent, #22c55e, #16a34a, transparent)',
        backgroundSize: '200% auto', animation: `${shimmer} 3s linear infinite`,
      }} />

      {/* Logo + collapse toggle */}
      <Stack direction="row" alignItems="center" justifyContent={collapsed ? 'center' : 'space-between'}
        sx={{ px: collapsed ? 1 : 2.5, py: 2.2, flexShrink: 0 }}>
        {!collapsed && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{
              width: 26, height: 26, borderRadius: '7px',
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(34,197,94,.25)',
            }}>
              <SportsSoccerIcon sx={{ color: '#fff', fontSize: 16 }} />
            </Box>
            <Typography sx={{
              fontWeight: 900, fontSize: '1rem', letterSpacing: -.4,
              background: 'linear-gradient(135deg, #15803d, #22c55e)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              ProPlayeur
            </Typography>
          </Stack>
        )}
        <IconButton size="small" onClick={() => setCollapsed(v => !v)}
          sx={{ color: '#94a3b8', '&:hover': { color: '#16a34a', bgcolor: 'rgba(34,197,94,.06)' } }}>
          {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </Stack>

      {/* User avatar card */}
      <Box sx={{
        mx: collapsed ? 1 : 2, mb: 2, p: collapsed ? 1 : 2, borderRadius: 3,
        bgcolor: 'rgba(34,197,94,.05)', border: '1px solid rgba(34,197,94,.12)',
        display: 'flex', flexDirection: collapsed ? 'column' : 'row',
        alignItems: 'center', gap: 1.5, flexShrink: 0,
      }}>
        <Avatar sx={{
          width: collapsed ? 36 : 44, height: collapsed ? 36 : 44,
          background: `linear-gradient(135deg, ${ROLE_COLOR[role]}33, ${ROLE_COLOR[role]}55)`,
          border: `2px solid ${ROLE_COLOR[role]}55`,
          color: ROLE_COLOR[role], fontWeight: 800,
          fontSize: collapsed ? '.9rem' : '1.1rem', flexShrink: 0,
        }}>
          {user.firstName?.[0]}{user.lastName?.[0]}
        </Avatar>
        {!collapsed && (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{
              color: '#0f172a', fontWeight: 700,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {user.firstName} {user.lastName}
            </Typography>
            <Chip label={ROLE_LABEL[role] || role} size="small" sx={{
              bgcolor: `${ROLE_COLOR[role]}18`, color: ROLE_COLOR[role],
              fontWeight: 700, fontSize: '.65rem', height: 18, mt: .3,
            }} />
          </Box>
        )}
      </Box>

      {/* Nav items */}
      <List sx={{ px: collapsed ? .5 : 1.5, flex: 1 }}>
        {NAV.map(item => {
          const active = activeSection === item.key;
          return (
            <Tooltip key={item.key} title={collapsed ? item.label : ''} placement="right">
              <ListItemButton onClick={() => { onNavigate(item.key); onMobileClose?.(); }}
                sx={{
                  borderRadius: 2.5, mb: .5, px: collapsed ? 1.5 : 2, py: 1.2,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  bgcolor: active ? 'rgba(34,197,94,.08)' : 'transparent',
                  border: active ? '1px solid rgba(34,197,94,.25)' : '1px solid transparent',
                  '&:hover': { bgcolor: 'rgba(34,197,94,.06)', border: '1px solid rgba(34,197,94,.15)' },
                  transition: 'all .2s',
                }}>
                <ListItemIcon sx={{
                  color: active ? '#16a34a' : '#94a3b8',
                  minWidth: collapsed ? 0 : 38, transition: 'color .2s',
                }}>
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText primary={item.label} primaryTypographyProps={{
                    sx: {
                      color: active ? '#16a34a' : '#475569',
                      fontWeight: active ? 700 : 500, fontSize: '.88rem',
                    },
                  }} />
                )}
                {!collapsed && active && (
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  }} />
                )}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>

      {/* Logout */}
      <Divider sx={{ borderColor: 'rgba(34,197,94,.1)', mx: 2, mb: 1 }} />
      <Box sx={{ px: collapsed ? .5 : 1.5, pb: 2 }}>
        <Tooltip title={collapsed ? 'Déconnexion' : ''} placement="right">
          <ListItemButton onClick={handleLogout} sx={{
            borderRadius: 2.5, px: collapsed ? 1.5 : 2, py: 1.2,
            justifyContent: collapsed ? 'center' : 'flex-start',
            '&:hover': { bgcolor: 'rgba(239,68,68,.06)' }, transition: 'all .2s',
          }}>
            <ListItemIcon sx={{ color: 'rgba(239,68,68,.5)', minWidth: collapsed ? 0 : 38 }}>
              <LogoutOutlinedIcon />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText primary="Déconnexion" primaryTypographyProps={{
                sx: { color: 'rgba(239,68,68,.65)', fontSize: '.88rem' },
              }} />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: { xs: 'none', md: 'block' }, width, flexShrink: 0, transition: 'width .3s' }}>
        <Box sx={{ position: 'fixed', top: 0, left: 0, height: '100vh', width, zIndex: 1200, transition: 'width .3s' }}>
          {content}
        </Box>
      </Box>

      <Drawer open={mobileOpen} onClose={onMobileClose} variant="temporary"
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: SIDEBAR_W, border: 'none' } }}>
        {content}
      </Drawer>
    </>
  );
};

export default Sidebar;